import React from 'react';
import { NumberInputRow } from '@/components/ui';
import { Switch } from '@/components/ui/switch';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { CustomizedFieldIndicator } from '../../form/CustomizedFieldIndicator';
import { AnimatedFormError } from '../../form/AnimatedFormError';
import { fieldLabelClass, fieldDescriptionClass } from '@/components/config_forms/shared/surcharge/fieldStyles';

// Generic config type that includes the hourly premium fields
type HourlyPremiumConfig = {
  premiumType?: 'percentage' | 'fixed' | null;
  premiumValue?: number | null;
  premiumBasis?: 'base_rate' | 'ot_inclusive';
};

const PREMIUM_TYPE_OPTIONS = [
  { value: 'fixed', label: 'Dollar' },
  { value: 'percentage', label: 'Percentage' },
];

interface Props {
  config: HourlyPremiumConfig;
  onConfigChange: (updates: Partial<HourlyPremiumConfig>) => void;
  premiumValueProps?: Record<string, unknown>;
  premiumValueError?: React.ReactNode;
  customizedFields?: Partial<Record<string, boolean>>;
  context?: 'user' | 'client';
  /** Override default field descriptions (e.g., for count-mode meal break context) */
  descriptionOverrides?: {
    premiumAmountFixed?: string;
    premiumAmountFixedOT?: string;
    premiumAmountPercentage?: string;
    premiumAmountPercentageOT?: string;
  };
}

export const HourlyPremiumSection: React.FC<Props> = ({
  config,
  onConfigChange,
  premiumValueProps,
  premiumValueError,
  customizedFields = {},
  context = 'client',
  descriptionOverrides,
}) => {
  const isPercentage = (config.premiumType ?? 'fixed') === 'percentage';

  return (
    <>
      <div className="grid grid-cols-1 gap-y-1 md:grid-cols-2 md:gap-4 md:items-center">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className={fieldLabelClass}>Premium Type</span>
            <CustomizedFieldIndicator isCustomized={customizedFields.premiumType} context={context} />
          </div>
          <p className={`${fieldDescriptionClass} hidden md:block`}>A dollar-per-hour surcharge rate, or a percentage of your hourly rate.</p>
        </div>
        <div className="flex justify-start md:justify-end md:col-start-2">
          <SegmentedControl
            ariaLabel="Premium Type"
            options={PREMIUM_TYPE_OPTIONS}
            value={config.premiumType ?? 'fixed'}
            onChange={(v) => {
              onConfigChange({ premiumType: v as 'fixed' | 'percentage' });
            }}
            className="w-full md:w-auto md:min-w-[260px]"
          />
        </div>
        <p className={`${fieldDescriptionClass} md:hidden`}>A dollar-per-hour surcharge rate, or a percentage of your hourly rate.</p>
      </div>

      {!isPercentage ? (
        <NumberInputRow
          id="premium-value-dollar"
          label="Hourly Premium"
          description={config.premiumBasis === 'ot_inclusive'
            ? (descriptionOverrides?.premiumAmountFixedOT ?? "This amount is added per hour, scaled by your current overtime rate.")
            : (descriptionOverrides?.premiumAmountFixed ?? "This amount is added per hour during the surcharge period.")}
          unit="$"
          unitPosition="left"
          inputProps={premiumValueProps ?? {}}
          errorElement={<AnimatedFormError>{premiumValueError}</AnimatedFormError>}
        />
      ) : (
        <NumberInputRow
          id="premium-value-percentage"
          label="Hourly Premium"
          description={config.premiumBasis === 'ot_inclusive'
            ? (descriptionOverrides?.premiumAmountPercentageOT ?? "Applied to your current hourly rate — increases during overtime hours.")
            : (descriptionOverrides?.premiumAmountPercentage ?? "Applied to your base hourly rate — does not scale as overtime is applied.")}
          unit="%"
          inputProps={premiumValueProps ?? {}}
          errorElement={<AnimatedFormError>{premiumValueError}</AnimatedFormError>}
        />
      )}

      <div className="w-full grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1 items-center md:grid-cols-2 md:gap-4">
        <div className="min-w-0 space-y-1">
          <div className="flex items-center gap-2">
            <span className={fieldLabelClass}>Scale with Overtime</span>
            <CustomizedFieldIndicator isCustomized={customizedFields.premiumBasis} context={context} />
          </div>
          <p className={`${fieldDescriptionClass} hidden md:block`}>
            Scale the premium by the active overtime multiplier.
          </p>
        </div>
        <div className="flex items-center justify-end md:w-full">
          <Switch
            id="premium-basis-ot"
            checked={(config.premiumBasis ?? 'base_rate') === 'ot_inclusive'}
            onCheckedChange={(checked) => onConfigChange({ premiumBasis: checked ? 'ot_inclusive' : 'base_rate' })}
          />
        </div>
        <p className={`${fieldDescriptionClass} col-span-2 md:hidden`}>
          Scale the premium by the active overtime multiplier.
        </p>
      </div>
    </>
  );
};
